const CACHE_TTL = 5 * 60 * 1000
const cache = new Map()
const pendingRequests = new Map()

const isFresh = entry => entry && Date.now() - entry.createdAt < CACHE_TTL

export async function getCachedIndicationResult (key, request) {
  const cached = cache.get(key)
  if (isFresh(cached)) return cached.value
  if (pendingRequests.has(key)) return pendingRequests.get(key)

  const promise = Promise.resolve(request())
    .then(value => {
      cache.set(key, { value, createdAt: Date.now() })
      return value
    })
    .finally(() => pendingRequests.delete(key))

  pendingRequests.set(key, promise)
  return promise
}

export function getLisResultCacheKey (indicationIds) {
  return `lis:${[...indicationIds].map(String).sort().join(',')}`
}

export function getIndicationDetailCacheKey (indicationId) {
  return `indication-detail:${indicationId}`
}

export async function getCachedLisResultData (indicationIds, request) {
  const ids = [...indicationIds].filter(Boolean)
  const cachedResults = ids.map(id => cache.get(`lis-indication:${id}`))
  if (cachedResults.length && cachedResults.every(isFresh)) {
    const first = cachedResults[0]
    return {
      data: {
        Data: cachedResults.map(entry => entry.value.group),
        meta: first.value.meta || {}
      }
    }
  }

  const response = await getCachedIndicationResult(getLisResultCacheKey(ids), request)
  const groups = response?.data?.Data || []
  groups.forEach(group => {
    if (group?.indication_id) {
      cache.set(`lis-indication:${group.indication_id}`, {
        value: { group, meta: response?.data?.meta || {} },
        createdAt: Date.now()
      })
    }
  })
  return response
}

// Keep the current browser session coherent after a local result edit. Refresh
// only the affected entries so the edited result remains cached for five
// minutes; the UI still applies localStorage as the final source of priority.
export function updateCachedLisResultData (indicationId, updateGroup) {
  const id = String(indicationId)
  cache.forEach((entry, key) => {
    if (!entry || !entry.value) return

    if (key === `lis-indication:${id}` && entry.value.group) {
      entry.value = { ...entry.value, group: updateGroup(entry.value.group) }
      entry.createdAt = Date.now()
      return
    }

    if (!key.startsWith('lis:') || !entry.value?.data?.Data) return
    const groups = entry.value.data.Data
    if (!groups.some(group => String(group?.indication_id) === id)) return
    entry.value = {
      ...entry.value,
      data: {
        ...entry.value.data,
        Data: groups.map(group => String(group?.indication_id) === id ? updateGroup(group) : group)
      }
    }
    entry.createdAt = Date.now()
  })
}
